import { useRef, useState } from "react";
import { parseAddresses } from "../../helpers";
import { createCustomerAddress, deleteCustomerAddress, updateCustomerAddress } from "../../models/shared/customersModel";
import { normalizeAddressForEditor, buildAddressSavePayload } from "../../models/customer/customerDraftModel";
import { addressEditorForm, locationStepError, missingAddressFields } from '../../models/customer/structuredAddressModel';
import { newRequestKey } from '../../../messaging/requestKey';

export function useCustomerAddresses({ customer, onFieldChanged, message }) {
  const [addresses, setAddresses] = useState([]);
  const [subForm, setSubForm] = useState(null);
  const [addressSaving, setAddressSaving] = useState(false);
  const [addressError, setAddressError] = useState('');
  const [operationsImpact, setOperationsImpact] = useState(null);
  const [operationsReason, setOperationsReason] = useState('');
  const savingRef = useRef(false);
  const [deletingAddressId, setDeletingAddressId] = useState(null);
  const resetAddresses = () => {
    setAddresses(
      parseAddresses(customer.customer_addresses_new ?? customer.addresses)
        .map(normalizeAddressForEditor)
        .map((a, i) => a.id != null ? a : { ...a, id: `existing-${i}` })
    );
    setSubForm(null);
    setOperationsImpact(null);
  };
  const publishAddresses = (nextAddresses) => {
    setAddresses(nextAddresses);
    onFieldChanged?.({ customer_id: customer.customer_id, customer_addresses_new: nextAddresses });
  };

  const editAddress = (addr) => { setAddressError(''); setSubForm(addressEditorForm(addr)); };

  const handleDeleteAddress = async (id) => {
    if (!customer?.customer_id || id == null) return;
    setDeletingAddressId(id);
    try {
      const res = await deleteCustomerAddress(customer.customer_id, id);
      if (!res.success) throw new Error(res.message);
      const nextAddresses = addresses.filter(a => a.id !== id);
      publishAddresses(nextAddresses);
      if (subForm?.id === id) setSubForm(null);
      message.success({ content: "Address deleted", duration: 2 });
    } catch (e) {
      message.error(e.message || "Failed to delete address");
    } finally {
      setDeletingAddressId(null);
    }
  };

  const saveSubForm = async (confirmation = null) => {
    const pinError = locationStepError(subForm || {});
    setAddressError(pinError);
    if (pinError || savingRef.current) return;
    const missing = missingAddressFields(subForm);
    if (missing.length) {
      setSubForm(form => ({ ...form, _serverMissing: missing }));
      message.warning("Complete the required address details");
      return;
    }
    if (!customer?.customer_id) return;
    const payload = confirmation?.payload || buildAddressSavePayload(subForm);
    const isExisting = subForm.id != null && addresses.some(a => a.id === subForm.id);
    setAddressSaving(true);
    savingRef.current = true;
    try {
      const res = isExisting
        ? await updateCustomerAddress(customer.customer_id, subForm.id, payload)
        : await createCustomerAddress(customer.customer_id, payload);
      if (!res.success || !res.data) throw Object.assign(new Error(res.message), { response: { data: res } });
      const savedAddress = normalizeAddressForEditor(isExisting ? res.data.address : res.data);
      if (!savedAddress || typeof savedAddress !== 'object' || savedAddress.id == null) throw new Error('Invalid address response: the saved address is missing. Reload before trying again.');
      const nextAddresses = isExisting
        ? addresses.map(a => a.id === subForm.id ? savedAddress : a)
        : [...addresses, savedAddress];
      publishAddresses(nextAddresses);
      setSubForm(null);
      setOperationsImpact(null);
      message.success({ content: isExisting ? "Address updated" : "Address added", duration: 2 });
    } catch (e) {
      const failure = e.response?.data || {};
      if (failure.code === 'ADDRESS_DETAILS_REQUIRED') {
        setSubForm(form => ({ ...form, _step: 2, _serverMissing: (failure.data?.missing || []).map(key => key === 'phone_number' ? 'number' : key) }));
      }
      if (isExisting && failure.data?.affected_operations_lines?.length) {
        const key = operationsImpact?.key || newRequestKey();
        setOperationsImpact(old => old || { payload, lines: failure.data.affected_operations_lines, key });
        setOperationsReason('');
      }
      setAddressError(failure.message || e.message || 'Could not save address');
      message.error(e.message || ("Failed to " + (isExisting ? "update" : "add") + " address"));
    } finally {
      setAddressSaving(false);
      savingRef.current = false;
    }
  };


  const confirmOperationsImpact = () => {
    if (!operationsImpact || !operationsReason.trim()) return;
    return saveSubForm({ payload: { ...operationsImpact.payload, confirm_operations_impact: true,
      operations_change_reason: operationsReason.trim(), idempotency_key: operationsImpact.key } });
  };
  return { addresses, subForm, setSubForm, addressSaving, addressError, deletingAddressId, resetAddresses, editAddress, handleDeleteAddress,
    saveSubForm, operationsImpact, operationsReason, setOperationsReason, confirmOperationsImpact,
    cancelOperationsImpact: () => setOperationsImpact(null) };
}
